///////////////////////////////////////////////////////////////////////////////////////
//                          Copyright (c) 2020 - 2021
//                           Coyote Logistics L.L.C.
//                         All Rights Reserved Worldwide
//
//WARNING:  This program (or document) is unpublished, proprietary
//property of Coyote Logistics L.L.C. and is to be maintained in strict confidence.
//Unauthorized reproduction, distribution or disclosure of this program
//(or document), or any program (or document) derived from it is
//prohibited by State and Federal law, and by local law outside of the U.S.
///////////////////////////////////////////////////////////////////////////////////////

import React, { useState } from "react";
import { useDispatch } from "react-redux";

import Modal from "../../Modal";
import OutlinedInput from "../../form-controls/OutlinedInput";
import { applyMassMargin } from "../../../slices/bidPricingSlice";
import ignoreNullValues from "../../../helpers/ignoreNullValues";
import { patterns } from "../../../helpers/types";
import messages from "../../../helpers/messages";
import { disableMatchingLanes } from "../../../slices/laneMatchSlice";
import getCurrency from "../../../helpers/getCurrency";

export const ApplyMassMarginModal = ({ closeModal, open, role, division }) => {
  const dispatch = useDispatch();
  const initialState = {
    flatMarginPct: "",
    minMargin: "",
    maxMargin: ""
  };
  const [massMarginFields, setMassMarginFields] = useState(initialState);

  const handleValidation = ({ target }) => {
    if (
      isNaN(parseFloat(target.value, 10)) ||
      parseFloat(target.value, 10) > parseFloat(target.max) ||
      parseFloat(target.value, 10) < parseFloat(target.min)
    ) {
      setMassMarginFields({
        ...massMarginFields,
        [target.id]: ""
      });
    } else {
      setMassMarginFields({
        ...massMarginFields,
        [target.id]: parseFloat(target.value, 10)
      });
    }
  };

  const handleChange = ({ target }, pattern) => {
    if (target.value.match(pattern)) {
      setMassMarginFields({
        ...massMarginFields,
        [target.id]: target.value
      });
    }
  };

  const handleExit = () => {
    setMassMarginFields(initialState);
    closeModal();
  };

  const handleApply = () => {
    dispatch(applyMassMargin({ massMarginFields, role }));
    dispatch(disableMatchingLanes());
    handleExit();
  };

  return (
    <Modal
      title={messages.applyMassMargin.defaultMessage}
      open={open}
      handleClose={handleExit}
      modalStyle={{ width: "354px", height: "318px" }}
      titleClass="modal__title_light uppercase"
      bodyClass="border-top-grey px-24 pt-24"
      closeButtonClass="modal__close-button mr-16 mt-16"
    >
      <div className="grid">
        <div className="grid__cell one-whole pb-8">
          <OutlinedInput
            id="flatMarginPct"
            value={massMarginFields.flatMarginPct}
            label={messages.flatMarginPercentage.defaultMessage}
            unit="%"
            onBlur={handleValidation}
            onChange={e => handleChange(e, patterns.intTwoFractionalTwo)}
          />
        </div>
        <div className="grid__cell one-whole pb-8">
          <OutlinedInput
            id="minMargin"
            value={massMarginFields.minMargin}
            label={messages.minimumMargin.defaultMessage}
            unit={getCurrency(division)}
            isUnitPositionLeft={true}
            onBlur={handleValidation}
            onChange={e => handleChange(e, patterns.intSixFractionalTwo)}
            max={massMarginFields.maxMargin}
          />
        </div>
        <div className="grid__cell one-whole pb-8">
          <OutlinedInput
            id="maxMargin"
            value={massMarginFields.maxMargin}
            label={messages.maximumMargin.defaultMessage}
            unit={getCurrency(division)}
            isUnitPositionLeft={true}
            onBlur={handleValidation}
            onChange={e => handleChange(e, patterns.intSixFractionalTwo)}
            min={massMarginFields.minMargin}
          />
        </div>
        <div className="grid fr pt-4">
          <div className="grid__cell pr-8">
            <button
              data-testid="exit-button"
              className="secondary-button one-whole cursor-pointer univers-67-bold-cond short-button"
              onClick={handleExit}
            >
              {messages.exit.defaultMessage}
            </button>
          </div>
          <div className="grid__cell pl-8">
            <button
              data-testid="apply-button"
              className="primary-button one-whole cursor-pointer univers-67-bold-cond short-button"
              onClick={handleApply}
              disabled={
                Object.keys(ignoreNullValues(massMarginFields)).length !==
                Object.keys(massMarginFields).length
              }
            >
              {messages.apply.defaultMessage}
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default ApplyMassMarginModal;
